/**
 * Warms a step's lazy card chunks (drill / solve / info) before the step view
 * needs them, so opening a step or its INSTRUCTIONS dialog does not wait on a
 * chunk round trip. Prefetch only: no components are created and a failed
 * prefetch is swallowed (the real load path still handles errors).
 *
 * Where to call it:
 * - `prefetchForSteps(steps)` — when a section's step list renders
 *   (SectionStepsView); scheduled on idle, one prefetch per distinct kind.
 * - `prefetchForStep(step)` — on step click / navigation intent; runs now.
 * - `prefetchInfoForKind(stepKind)` — in StepView as soon as its kind is known,
 *   so the INSTRUCTIONS dialog's info card is warm before it opens.
 */
import type { StepTypeCamelCase } from 'src/types/course/step';
import { toPascalCase } from 'src/shared/utilities/string';
import {
  isDrillKind,
  resolveStepKind,
  type KindSourceStep,
} from '../utilities/drillStrategies/resolveDrillKind';
import { prefetchDrillChunks } from './useDrillComponents';

/**
 * A step as the section payload gives it: `step_type[0].__component` decides
 * the kind; a Filter step's real kind comes from `typePascalCase` (Step model)
 * or, for a plain payload object, from its `type` enum ('Single Choice' or
 * the model's kebab 'single-choice').
 */
export interface PrefetchSourceStep extends KindSourceStep {
  type?: string | null;
}

/**
 * The step's kind as resolveStepKind reads it (Filter → its real type),
 * limited to kinds that have chunks (a drill kind or Theory); else null.
 */
export function prefetchKindOf(
  step: PrefetchSourceStep | null | undefined,
): StepTypeCamelCase | null {
  if (!step) return null;
  const typePascalCase =
    step.typePascalCase ||
    (step.type ? (toPascalCase(step.type) as StepTypeCamelCase) : undefined);
  const kind = resolveStepKind({ step_type: step.step_type, typePascalCase });
  return kind === 'Theory' || isDrillKind(kind) ? kind : null;
}

function onIdle(task: () => void) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(task, { timeout: 2000 });
  } else {
    setTimeout(task, 1);
  }
}

export function useStepChunkPrefetch() {
  function prefetchForStep(step: PrefetchSourceStep | null | undefined) {
    const kind = prefetchKindOf(step);
    if (kind) void prefetchDrillChunks(kind);
  }

  function prefetchForSteps(
    steps: ReadonlyArray<PrefetchSourceStep | null | undefined>,
  ) {
    const kinds = new Set<StepTypeCamelCase>();
    for (const step of steps) {
      const kind = prefetchKindOf(step);
      if (kind) kinds.add(kind);
    }
    if (kinds.size === 0) return;
    onIdle(() => {
      for (const kind of kinds) void prefetchDrillChunks(kind);
    });
  }

  function prefetchInfoForKind(stepKind: StepTypeCamelCase | null | undefined) {
    if (stepKind) void prefetchDrillChunks(stepKind, ['info']);
  }

  return { prefetchForStep, prefetchForSteps, prefetchInfoForKind };
}
